import { type ComponentProps, type ReactNode, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Stack, Typography, type SxProps, type Theme } from "@mui/material";
import type { TooltipProps } from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import { FoodsIconButton, FoodsTags, FoodsTooltip, WhiteBox, type IconMapping } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

type StatCardProps = {
    accentColor: string;
    highlighted?: boolean;
    icon: ReactNode;
    label: string;
    value: number;
};

export const StatCard = ({ accentColor, highlighted, icon, label, value }: StatCardProps) => {
    const theme = useTheme();

    return (
        <WhiteBox
            padding={theme.spacing(2.5)}
            boxShadow={theme.custom.shadows.grey}
            sx={{
                flex: 1,
                minWidth: { xs: "100%", md: "180px" },
                position: "relative",
                overflow: "hidden",
                fontFamily: theme.typography.fontFamily,
                ...(highlighted && {
                    bgcolor: alpha(accentColor, 0.06),
                    borderTop: `3px solid ${accentColor}`,
                }),
            }}
        >
            <Stack direction="row" alignItems="center" gap={1.5}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: alpha(accentColor, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            fontSize: "22px",
                            lineHeight: 1,
                            color: accentColor,
                        }}
                    >
                        {value}
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{ color: theme.custom.grey[2000], lineHeight: 1.3, display: "block", mt: "2px" }}
                    >
                        {label}
                    </Typography>
                </Box>
            </Stack>
        </WhiteBox>
    );
};

type SectionHeaderProps = {
    accentColor: string;
    count: number;
    title: string;
};

export const SectionHeader = ({ accentColor, count, title }: SectionHeaderProps) => {
    const theme = useTheme();

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{
                pl: theme.spacing(2),
                borderLeft: `4px solid ${accentColor}`,
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: theme.custom.grey[2000],
                    fontSize: "11px",
                }}
            >
                {title}
            </Typography>
            <Typography
                variant="overline"
                sx={{
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: theme.custom.grey[2000],
                    fontSize: "11px",
                }}
            >
                — {count}
            </Typography>
        </Stack>
    );
};

type ConsentActionIconButtonProps = {
    color?: string;
    disabled?: boolean;
    disabledTooltip?: ReactNode;
    icon: keyof IconMapping;
    label: string;
    onClick: () => void;
};

export const ConsentActionIconButton = ({
    color,
    disabled = false,
    disabledTooltip,
    icon,
    label,
    onClick,
}: ConsentActionIconButtonProps) => {
    const theme = useTheme();
    const button = (
        <FoodsIconButton
            size="s"
            icon={icon}
            withBackground
            tooltip={disabled && disabledTooltip ? undefined : { text: label, props: { placement: "top" } }}
            buttonProps={{
                "aria-label": label,
                disabled,
                onClick,
                sx: {
                    color: color ?? theme.palette.primary.main,
                    height: 44,
                    minWidth: 44,
                    width: 44,
                    "&.Mui-disabled": {
                        color: theme.custom.grey[1400],
                    },
                    "&:focus-visible": {
                        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}`,
                    },
                },
            }}
        />
    );

    if (disabled && disabledTooltip) {
        return (
            <FoodsTooltip title={disabledTooltip} placement="top" width="auto" arrow>
                <Box component="span" sx={{ display: "inline-flex" }}>
                    {button}
                </Box>
            </FoodsTooltip>
        );
    }

    return button;
};

type EllipsisTooltipProps = {
    children: ReactNode;
    title: ReactNode;
    placement?: TooltipProps["placement"];
    sx?: SxProps<Theme>;
    width?: string;
};

export const EllipsisTooltip = ({
    children,
    placement = "top-start",
    sx,
    title,
    width = "max-content",
}: EllipsisTooltipProps) => {
    const contentRef = useRef<HTMLSpanElement | null>(null);
    const [hasOverflow, setHasOverflow] = useState(false);

    useEffect(() => {
        const node = contentRef.current;

        if (!node) {
            return undefined;
        }

        const updateOverflow = () => {
            setHasOverflow(node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight);
        };

        updateOverflow();

        const frame = typeof window !== "undefined" ? window.requestAnimationFrame(updateOverflow) : undefined;
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateOverflow) : undefined;

        observer?.observe(node);

        return () => {
            observer?.disconnect();

            if (frame !== undefined && typeof window !== "undefined") {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [children, title]);

    const content = (
        <Box
            ref={contentRef}
            component="span"
            sx={{
                display: "block",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ...sx,
            }}
        >
            {children}
        </Box>
    );

    if (!hasOverflow || title === undefined || title === null || title === "") {
        return content;
    }

    return (
        <FoodsTooltip
            title={title}
            placement={placement}
            width={width}
            arrow
            enterDelay={500}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
        >
            {content}
        </FoodsTooltip>
    );
};

type EllipsisFoodsTagProps = ComponentProps<typeof FoodsTags> & {
    tooltipTitle?: ReactNode;
    tooltipWidth?: string;
};

export const EllipsisFoodsTag = ({
    label,
    sx,
    tooltipTitle = label,
    tooltipWidth = "max-content",
    ...props
}: EllipsisFoodsTagProps) => {
    const tagRef = useRef<HTMLSpanElement | null>(null);
    const [hasOverflow, setHasOverflow] = useState(false);

    useEffect(() => {
        const rootNode = tagRef.current;

        if (!rootNode) {
            return undefined;
        }

        const updateOverflow = () => {
            const textNode = rootNode.querySelector(".MuiTypography-root") as HTMLElement | null;
            const measuredNode = textNode ?? rootNode;

            setHasOverflow(measuredNode.scrollWidth > measuredNode.clientWidth);
        };

        updateOverflow();

        const frame = typeof window !== "undefined" ? window.requestAnimationFrame(updateOverflow) : undefined;
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateOverflow) : undefined;

        observer?.observe(rootNode);

        return () => {
            observer?.disconnect();

            if (frame !== undefined && typeof window !== "undefined") {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [label, tooltipTitle]);

    const content = (
        <Box ref={tagRef} component="span" sx={{ display: "inline-flex", maxWidth: "100%", minWidth: 0 }}>
            <FoodsTags
                {...props}
                label={label}
                sx={
                    {
                        maxWidth: "100%",
                        minWidth: 0,
                        overflow: "hidden",
                        "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                            flexShrink: 0,
                        },
                        "& .MuiTypography-root": {
                            display: "block",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        },
                        ...sx,
                    } as ComponentProps<typeof FoodsTags>["sx"]
                }
            />
        </Box>
    );

    if (!hasOverflow || tooltipTitle === undefined || tooltipTitle === null || tooltipTitle === "") {
        return content;
    }

    return (
        <FoodsTooltip
            title={tooltipTitle}
            placement="top-start"
            width={tooltipWidth}
            arrow
            enterDelay={500}
            enterTouchDelay={0}
            leaveTouchDelay={3000}
        >
            {content}
        </FoodsTooltip>
    );
};

export const getConsentDisabledActionButtonSx = (theme: Theme, color: string = theme.custom.grey[2000]) => ({
    "&.Mui-disabled, &:disabled": {
        opacity: 1,
        color,
        background: `${theme.palette.action.disabledBackground} !important`,
        backgroundImage: "none",
        boxShadow: "none",
        "& .MuiCircularProgress-root": {
            color: "inherit",
        },
        "& svg": {
            color: "inherit",
        },
    },
});

export const TabLabel = ({ count, label }: { count?: number; label: string }) => (
    <Stack direction="row" alignItems="center" gap={0.75}>
        <span>{label}</span>
        {count !== undefined && (
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 20,
                    height: 20,
                    borderRadius: "10px",
                    bgcolor: (theme) => alpha(theme.palette.common.black, 0.08),
                    fontSize: "11px",
                    fontWeight: 700,
                    lineHeight: 1,
                    px: "6px",
                }}
            >
                {count}
            </Box>
        )}
    </Stack>
);

type BulkActionBarProps = {
    children: ReactNode;
    count: number;
    loading?: boolean;
    onClear: () => void;
};

export const BulkActionBar = ({ children, count, loading = false, onClear }: BulkActionBarProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (count === 0) {
        return null;
    }

    return (
        <WhiteBox
            padding={theme.spacing(1.25, 1.5)}
            boxShadow={theme.custom.shadows.grey}
            sx={{
                bgcolor: theme.palette.common.white,
                border: `1px solid ${theme.custom.grey[500]}`,
                borderRadius: theme.spacing(1.5),
                position: "sticky",
                top: theme.spacing(1),
                zIndex: 10,
            }}
        >
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} gap={1.5}>
                <Stack direction="row" alignItems="center" gap={1} flex={1} flexWrap="wrap" sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: theme.custom.grey[2000], fontWeight: 700 }}>
                        {t("settings.consent.selection.count", "{{count}} selected", { count })}
                    </Typography>
                    {loading && (
                        <Stack direction="row" alignItems="center" gap={0.75}>
                            <CircularProgress size={14} thickness={5} sx={{ color: theme.custom.grey[2000] }} />
                            <Typography variant="caption" sx={{ color: theme.custom.grey[2000], fontWeight: 700 }}>
                                {t("common.loading", "Loading...")}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    {children}
                    <FoodsIconButton
                        size="m"
                        icon="close"
                        withBackground
                        tooltip={{
                            text: t("settings.consent.selection.clear", "Clear selection"),
                            props: { placement: "top" },
                        }}
                        buttonProps={{
                            "aria-label": t("settings.consent.selection.clear", "Clear selection"),
                            onClick: onClear,
                        }}
                    />
                </Stack>
            </Stack>
        </WhiteBox>
    );
};
